// src/utils/teams.js
//
// Teams sit INSIDE a company. A company can be split into up to 5 teams, each
// with exactly one Main Admin, plus dispatchers and drivers.
//
// Teams are an identity and grouping layer, NOT an access boundary:
//
//   • A driver or dispatcher with no team stays visible to their whole company,
//     exactly as before. Creating a team can never hide anyone who was visible.
//   • Access is still decided by company (parentCompanyId / assignedParentCompanyIds)
//     and by manual driver grants.
//
// What teams give you is a clear "this is mine" signal as a Main Admin moves
// between Current Loads, Accounting, Drivers and Dispatchers.
//
// Storage — no new collections, no security rule changes:
//   companies/{id}.teams  = [{ id, name, color }]     (max 5)
//   drivers/{id}.teamId   = string | null              (a driver has one company)
//   users/{id}.teamIds    = string[]                   (a user can span companies)

export const MAX_TEAMS = 5;

// Distinct, colour-blind-friendly hues. Index follows team order so a team keeps
// its colour as long as it exists.
export const TEAM_COLORS = [
  { key: 'blue',   dot: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
  { key: 'amber',  dot: '#d97706', bg: '#fffbeb', border: '#fde68a', text: '#b45309' },
  { key: 'teal',   dot: '#0d9488', bg: '#f0fdfa', border: '#99f6e4', text: '#0f766e' },
  { key: 'purple', dot: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', text: '#6d28d9' },
  { key: 'rose',   dot: '#e11d48', bg: '#fff1f2', border: '#fecdd3', text: '#be123c' },
];

// Night versions: a faint wash of the team's colour on the dark page instead of
// a pale pastel box, and lighter text so the team name stays readable. Same
// approach as appTheme.css takes for bg-red-50 and friends; these colours are
// set inline, so the stylesheet cannot reach them.
const NIGHT_COLORS = {
  blue:   { bg: 'rgba(37,99,235,0.14)',  border: 'rgba(37,99,235,0.45)',  text: '#93c5fd' },
  amber:  { bg: 'rgba(217,119,6,0.14)',  border: 'rgba(217,119,6,0.45)',  text: '#fcd34d' },
  teal:   { bg: 'rgba(13,148,136,0.14)', border: 'rgba(13,148,136,0.45)', text: '#5eead4' },
  purple: { bg: 'rgba(124,58,237,0.14)', border: 'rgba(124,58,237,0.45)', text: '#c4b5fd' },
  rose:   { bg: 'rgba(225,29,72,0.14)',  border: 'rgba(225,29,72,0.45)',  text: '#fda4af' },
};

export const getTeamColor = (colorKey, fallbackIndex = 0, isNight = false) => {
  const c = TEAM_COLORS.find(x => x.key === colorKey) || TEAM_COLORS[fallbackIndex % TEAM_COLORS.length];
  return isNight ? { ...c, ...NIGHT_COLORS[c.key] } : c;
};

/** Teams defined on a company document. */
export function getCompanyTeams(company) {
  const teams = company?.teams;
  return Array.isArray(teams) ? teams.filter(t => t && t.id) : [];
}

/** Every team across a set of companies, each carrying its company for context. */
export function getAllTeams(companies = []) {
  return companies.flatMap(company =>
    getCompanyTeams(company).map(team => ({
      ...team,
      companyId: company.id,
      companyName: company.name,
    }))
  );
}

export function findTeamById(companies = [], teamId) {
  if (!teamId) return null;
  return getAllTeams(companies).find(t => t.id === teamId) || null;
}

/**
 * The company a team belongs to, read from its id (see makeTeamId:
 * "<companyId>_team_<random>"). Null for anything not shaped like a team id.
 */
export function teamCompanyId(teamId) {
  if (typeof teamId !== 'string') return null;
  const at = teamId.indexOf('_team_');
  return at > 0 ? teamId.slice(0, at) : null;
}

/**
 * Team IDs a user belongs to.
 *
 * A team belongs to a company, so being on a team means being in that
 * company. A team whose company is not on the user's company list does not
 * count — it is left over from a move made on the Companies tab, which
 * changed the company and left the old team behind. Counting it put people on
 * load dropdowns of teams the Teams screen did not show them in.
 *
 * Records with no company list at all are left alone: they predate company
 * scoping, and hiding their teams could only take something away.
 */
export function getUserTeamIds(user) {
  const ids = Array.isArray(user?.teamIds) ? user.teamIds.filter(Boolean) : [];
  const companies = user?.assignedParentCompanyIds;
  if (!Array.isArray(companies)) return ids;
  return ids.filter(t => {
    const companyId = teamCompanyId(t);
    return !companyId || companies.includes(companyId);
  });
}

/** Is this user on this team (by the rule above)? */
export function isOnTeam(user, teamId) {
  return Boolean(teamId) && getUserTeamIds(user).includes(teamId);
}

/**
 * A driver's team, if it is one of their own company's teams. A teamId that
 * points at another company's team — left over from a company move — or at a
 * deleted team is no team at all.
 */
export function getDriverOwnTeamId(driver, companies = []) {
  const teamId = driver?.teamId;
  if (!teamId || !driver?.parentCompanyId) return null;
  const company = companies.find(c => c.id === driver.parentCompanyId);
  return getCompanyTeams(company).some(t => t.id === teamId) ? teamId : null;
}

export function getDriverTeamId(driver) {
  return driver?.teamId || null;
}

/**
 * The team a viewer should see their own work under.
 *
 * A Main Admin normally owns one team. If they somehow hold several, the first
 * is used for the "your team" marker — the others still resolve individually.
 */
export function getPrimaryTeamId(user) {
  return getUserTeamIds(user)[0] || null;
}

/** Does this record belong to any of the viewer's teams? */
export function belongsToViewerTeam(recordTeamId, viewerTeamIds = []) {
  if (!recordTeamId) return false;
  return viewerTeamIds.includes(recordTeamId);
}

/**
 * Generate an id for a new team. Prefixed with the company so a team id is
 * meaningful on its own when it turns up on a driver or user record.
 */
export function makeTeamId(companyId) {
  const random = Math.random().toString(36).slice(2, 8);
  return `${companyId}_team_${random}`;
}

/**
 * The colour a new team should take — the first one not already used in this
 * company, so teams stay visually distinct without anyone choosing.
 */
export function nextTeamColor(existingTeams = []) {
  const used = new Set(existingTeams.map(t => t.color));
  const free = TEAM_COLORS.find(c => !used.has(c.key));
  return (free || TEAM_COLORS[existingTeams.length % TEAM_COLORS.length]).key;
}

/**
 * Who is already the Main Admin of this team? Used to enforce exactly one.
 * `users` is the full tenant user list.
 */
export function findTeamMainAdmin(users = [], teamId) {
  return users.find(u => {
    if (!getUserTeamIds(u).includes(teamId)) return false;
    const roles = Array.isArray(u.role) ? u.role : [u.role].filter(Boolean);
    return roles.includes('Main Admin');
  }) || null;
}

/**
 * Dispatchers who run drivers on a team: Dispatcher role, not its Main Admin,
 * still active. `exceptId` leaves out someone who is on their way off it.
 */
export function teamDispatchers(users = [], teamId, exceptId = null) {
  return users.filter(u => {
    if (!u || u.id === exceptId || u.active === false) return false;
    const roles = Array.isArray(u.role) ? u.role : [u.role].filter(Boolean);
    return roles.includes('Dispatcher') && !roles.includes('Main Admin') && isOnTeam(u, teamId);
  });
}

/**
 * Who takes over a person's drivers when that person leaves teams.
 *
 * Every driver on a team they left and linked to them goes to the one other
 * dispatcher of that team, or to nobody when there are several to choose from
 * (the Teams screen then shows the driver as needing a dispatcher). A driver
 * left linked to someone off the team is how a removed dispatcher kept being
 * given that team's new loads: a new load copies the driver's dispatcher.
 *
 * Returns [{ driverId, dispatcherId }] — the writes to make.
 */
export function driverHandovers({ leavingUserId, leftTeamIds = [], drivers = [], users = [] }) {
  if (!leavingUserId || leftTeamIds.length === 0) return [];
  return drivers
    .filter(d => d && d.dispatcherId === leavingUserId && leftTeamIds.includes(d.teamId))
    .map(d => {
      const others = teamDispatchers(users, d.teamId, leavingUserId);
      return { driverId: d.id, dispatcherId: others.length === 1 ? others[0].id : null };
    });
}
